import { CopyOutlined } from '@ant-design/icons';
import { Flex, Form, Input, notification, Skeleton, Spin } from 'antd';
import { BaseButton } from 'components/button/button-base';
import { ErrorDisplay, ErrorListDisplay } from 'components/ErrorDisplay';
import { Text } from 'components/mvp-typography';
import { getUnixTime } from 'date-fns';
import { LoginLink } from 'lib/api/firebase/apis/api-login-link';
import { AssessmentIndexEntry } from 'lib/documents/patient-assessment/patient-assessment';
import { useAssessmentListQuery } from 'lib/hooks/tanstack/use-assessmentAPI';
import { useCopyToClipboardMutation } from 'lib/hooks/tanstack/use-CopyToClipboardMutation';
import {
  useGetLatestLoginLink,
  useGetLoginLinkToken,
  useLoginLinkAPI
} from 'lib/hooks/tanstack/use-loginLink';
import { useQueryErrorNotifier } from 'lib/hooks/use-query-error-notifier';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import {
  ErrorListContext,
  useErrorList,
  useErrorListContext
} from 'lib/utils/useErrorList';
import { sortBy } from 'lodash';
import { useMemo } from 'react';
import { generatePath } from 'react-router-dom';
import { Routes } from 'routes';
import { usePatientInfoContext } from '../../../../patient/_utils/patient-info/hook-context-patientinfo';
import { DisplayAssessmentsTableContent } from './display-assessment-links-table';
import { CreateAssessmentForm } from './form-create-assessment';

export function tokenToInviteLink(token: string): string {
  try {
    const url = new URL(window.location.origin);
    url.pathname = generatePath(Routes.AssessmentLinkEntrypoint, { token });
    return url.toString();
  } catch (e) {
    const _error = resolveError(e);
    const error = new Error(
      `[tokenToInviteLink] Failed to create an invite link for token ('${token}'). ${_error.message}`
    );

    throw error;
  }
}

export function ScheduleTestUI() {
  const patientInfo = usePatientInfoContext();
  const loginLinkAPI = useLoginLinkAPI();
  const assessments = useAssessmentListQuery(
    patientInfo.patientInfo.patient.id
  );
  const errorList = useErrorList();
  const sortedAssessments = useMemo(
    () =>
      sortBy(
        assessments.data?.data ?? [],
        a => -(a.metadata?.created.unix() ?? 0)
      ),
    [assessments.data]
  );

  const latestAssessment = sortedAssessments[0];
  useQueryErrorNotifier(loginLinkAPI.create, errorList);

  if (assessments.isLoading) {
    return <Skeleton active />;
  }

  return (
    <ErrorListContext value={{ errorList }}>
      {latestAssessment === undefined ||
      latestAssessment.status === 'completed' ||
      latestAssessment.status === 'cancelled' ||
      latestAssessment.status === 'expired' ? (
        <Flex vertical align="center" justify="center">
          <CreateAssessmentForm
            patientId={patientInfo.patientInfo.patient.id}
            onSuccess={data => {
              loginLinkAPI.create.mutate({
                uid: patientInfo.patientInfo.patient.id,
                useLimit: 10,
                expiresAt: data.expiresAt
              });
            }}
          />
        </Flex>
      ) : (
        <>
          <DisplayLoginLink
            uid={patientInfo.patientInfo.patient.id}
            latestAssessment={latestAssessment}
          />
        </>
      )}

      <ErrorListDisplay errorList={errorList} closeable />
    </ErrorListContext>
  );
}

function DisplayLoginLink(props: {
  uid: string;
  latestAssessment: AssessmentIndexEntry;
}) {
  const errorListContext = useErrorListContext();
  const latestLink = useGetLatestLoginLink(props.uid);
  const loginLinkAPI = useLoginLinkAPI();
  // Case 1: If the last link is expired, but the current active assessment is not, show an error and prompt the user to try again
  // Case 2: There is a login link
  if (latestLink.latest?.isActive) {
    return <CopyLoginLinkButton link={latestLink.latest} />;
  }

  if (latestLink.listQuery.isLoading) {
    return <Skeleton active />;
  }

  return (
    <ErrorDisplay
      error={{
        message:
          'The last link is expired or missing but the current assessment is not.'
      }}
      title="The last link is expired but the current assessment is not"
      description={
        <>
          <Text>Click here to generate a new link.</Text>
          <BaseButton
            onClick={() => {
              loginLinkAPI.create.mutate(
                {
                  uid: props.uid,
                  useLimit: 10,
                  expiresAt: getUnixTime(
                    props.latestAssessment.expiresAt!.toDate()
                  )
                },
                {
                  onError(e) {
                    errorListContext.errorList.addError(resolveError(e));
                  }
                }
              );
            }}
            loading={loginLinkAPI.create.isLoading}
          >
            Retry
          </BaseButton>
        </>
      }
    />
  );
}

// eslint-disable-next-line
function CopyLoginLinkButton(props: { link: LoginLink }) {
  const copyToClipboard = useCopyToClipboardMutation();
  function copyToClipboardWrapper(content: string) {
    copyToClipboard.mutate(content, {
      onSuccess() {
        notification.success({
          message:
            'Copied link to Clipboard. This is a sensitive link that will allow the recipient to start a ConcussionRx Assessment as the specified Test-Taker. ' +
            'Do not share it with anyone other than the intended Test-Taker.',
          showProgress: true,
          duration: 5
        });
      }
    });
  }

  const a = useGetLoginLinkToken(props.link.id);
  if (a.isLoading) {
    return <Spin />;
  }

  if (a.isError) {
    return <ErrorDisplay error={resolveError(a.error)} />;
  }

  const link = tokenToInviteLink(a.data?.data ?? '');
  return (
    <Flex vertical gap={16} style={{ width: '100%' }} align="center">
      <Text>Copyable invite link</Text>
      <div style={{ width: '50%' }}>
        <Form.Item>
          <Input
            data-private="redact"
            readOnly
            value={link}
            suffix={
              <CopyOutlined onClick={() => copyToClipboardWrapper(link)} />
            }
          />
        </Form.Item>
      </div>
      Used {props.link.usedCount}/{props.link.useLimit} times.
      <div>
        <BaseButton
          loading={copyToClipboard.isPending}
          onClick={() => copyToClipboardWrapper(link)}
        >
          Copy link
        </BaseButton>
      </div>
    </Flex>
  );
}

export function DisplayAssessmentsTable(props: { patientId: string }) {
  const assesmentLinks = useAssessmentListQuery(props.patientId);
  const errorList = useErrorList();
  useQueryErrorNotifier(assesmentLinks, errorList);
  if (assesmentLinks.isLoading) {
    return <Spin />;
  }

  return (
    <Flex gap={16} vertical align="center" style={{ width: '100%' }}>
      <div style={{ width: '100%' }}>
        <ErrorListContext value={{ errorList }}>
          <DisplayAssessmentsTableContent
            assessments={assesmentLinks.data?.data ?? []}
          />
        </ErrorListContext>

        <ErrorListDisplay errorList={errorList} />
      </div>
    </Flex>
  );
}
